import { useAnimation } from 'framer-motion';
import React, { createContext, useContext, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { originXYZero, pushTransition } from './transitions';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
export const AnimatedContext = createContext({});
const AnimatedProvider = (props) => {
    // State
    const location = useLocation();
    const firstRender = useRef(true);
    const bodyHeight = useRef(null);
    const store = {
        enter: null,
        exit: null,
    };
    const intermediaryImg = { el: useRef(null), animation: useAnimation() };
    const intermediaryWrapper = useRef(null);
    const playbackHooks = useRef({
        done: [],
        setup: [],
        animate: [],
        appMounted: [],
    });
    // Methods
    const subscribe = (key, cb) => {
        playbackHooks.current[key].push(cb);
    };
    const unsubscribe = (key, cb) => {
        playbackHooks.current[key] = [...playbackHooks.current[key].filter((entry) => entry !== cb)];
    };
    const setIntermediarySrc = async (exitSrc) => {
        const img = intermediaryImg.el.current;
        img.src = exitSrc;
        await img.decode();
    };
    const resetIntermediary = async () => {
        intermediaryWrapper.current.querySelector('.fade-backdrop').classList.remove('transitioning');
        intermediaryImg.el.current.classList.remove('shown');
        intermediaryImg.el.current.src = '';
        intermediaryImg.el.current.style.removeProperty('top');
        intermediaryImg.el.current.style.removeProperty('left');
        intermediaryImg.el.current.style.removeProperty('max-width');
        intermediaryImg.el.current.style.removeProperty('transform-origin');
        intermediaryImg.el.current.style.removeProperty('transform');
    };
    const resetStore = () => {
        store.exit = null;
        store.enter = null;
    };
    const skipAnimation = async (params) => {
        const { animate, done } = playbackHooks.current;
        const { enter, exit, transition, viewport } = params;
        const scrollY = (viewport === null || viewport === void 0 ? void 0 : viewport.scrollY) || 0;
        const header = document.getElementsByTagName('header')[0];
        const catalogContainer = document.getElementById('catalog');
        if ((transition === null || transition === void 0 ? void 0 : transition.direction) === 'out') {
            header === null || header === void 0 ? void 0 : header.classList.add('no-transition');
        }
        await Promise.all(animate.map(async (cb) => await cb(params)));
        enter.animation.set(Object.assign({ opacity: 1, pointerEvents: 'all', y: 0, x: 0, scale: 1 }, originXYZero));
        await Promise.all(done.map(async (cb) => await cb(params)));
        enter.animation.set({
            position: 'static',
        });
        catalogContainer.style.removeProperty('overflow');
        catalogContainer.style.removeProperty('height');
        header === null || header === void 0 ? void 0 : header.classList.remove('transitioning');
        document.documentElement.scrollTop = scrollY;
        exit.done();
        enter.done();
        resetStore();
        if (transition.direction === 'out') {
            header === null || header === void 0 ? void 0 : header.classList.remove('header--pdp');
            header === null || header === void 0 ? void 0 : header.classList.remove('no-transition');
        }
    };
    // SECTION - No Transition --------------------------------------------------------------------
    const noTransition = async (params) => {
        const { setup } = playbackHooks.current;
        const { enter, exit, transition } = params;
        const catalogContainer = document.getElementById('catalog');
        if (transition.direction === 'in') {
            bodyHeight.current = `${catalogContainer.offsetHeight}`;
        }
        else {
            catalogContainer.style.height = `${bodyHeight.current}px`;
        }
        const header = document.getElementsByTagName('header')[0];
        if (transition.direction === 'in') {
            header === null || header === void 0 ? void 0 : header.classList.add('header--pdp');
        }
        else {
            header === null || header === void 0 ? void 0 : header.classList.add('no-transition');
            header === null || header === void 0 ? void 0 : header.classList.remove('header--pdp');
        }
        enter.el.current.style.zIndex = '0';
        exit.el.current.style.zIndex = '1';
        exit.el.current.style.position = 'relative';
        enter.el.current.style.position = 'absolute';
        // Setup
        await Promise.all(setup.map(async (cb) => await cb(params)));
        skipAnimation(params);
    };
    // !SECTION End No transition
    // SECTION - Push --------------------------------------------------------------------
    const push = async (params) => {
        var _a, _b;
        const { animate, done } = playbackHooks.current;
        const { enter, exit, transition, viewport } = params;
        const catalogContainer = document.getElementById('catalog');
        if (transition.direction === 'in') {
            bodyHeight.current = `${catalogContainer.offsetHeight}`;
        }
        const header = document.getElementsByTagName('header')[0];
        if (window.innerWidth < window.mqObj['md'] && transition.direction === 'in') {
            header === null || header === void 0 ? void 0 : header.classList.add('header--pdp');
            header === null || header === void 0 ? void 0 : header.classList.add('transitioning');
        }
        else if (window.innerWidth > window.mqObj['md']) {
            header === null || header === void 0 ? void 0 : header.classList.remove('header--pdp');
        }
        const scale = (transition === null || transition === void 0 ? void 0 : transition.direction) === 'in' ? 0.7 : 1 / 0.7;
        const scrollY = (viewport === null || viewport === void 0 ? void 0 : viewport.scrollY) || 0;
        const exitScrollTop = window.scrollY;
        const winHeight = document.documentElement.clientHeight;
        const exitOriginY = winHeight / 2 + exitScrollTop;
        exit.el.current.style.zIndex = '2'; // Needed for PLP to be over PDP's accordions
        exit.el.current.style.position = 'fixed';
        exit.el.current.style.overflow = `hidden`;
        exit.el.current.style.scale = `1`;
        exit.el.current.style.opacity = `1`;
        const pushTransitionEase = pushTransition.transition.ease.join(',');
        exit.el.current.style.transition = `scale .75s cubic-bezier(${pushTransitionEase}), opacity .33s cubic-bezier(${pushTransitionEase})`;
        enter.el.current.style.transition = `opacity .5s cubic-bezier(${pushTransitionEase})`;
        enter.el.current.style.position = 'static';
        enter.el.current.style.zIndex = '0';
        enter.el.current.style.opacity = '0';
        enter.el.current.style.scale = '1';
        // Setup
        // await Promise.all(setup.map(async (cb: Function) => await cb(params)));
        //check if page is not ready
        if (!((_a = exit === null || exit === void 0 ? void 0 : exit.el) === null || _a === void 0 ? void 0 : _a.current) || !((_b = enter === null || enter === void 0 ? void 0 : enter.el) === null || _b === void 0 ? void 0 : _b.current)) {
            console.warn('ANIMATION SKIPPED FOR AJAX PROBLEM');
            skipAnimation(params);
            return;
        }
        await Promise.all(animate.map(async (cb) => await cb(params)));
        document.documentElement.scrollTop = scrollY;
        exit.el.current.style.top = `-${exitScrollTop}px`;
        exit.el.current.style.transformOrigin = `center ${exitOriginY}px`;
        //  exit.animation.start
        exit.el.current.style.scale = `${1 / scale}`;
        exit.el.current.style.opacity = `0`;
        await new Promise((resolve) => {
            setTimeout(() => {
                resolve(1);
            }, 750);
        });
        exit.done();
        enter.el.current.style.opacity = '1';
        // Clean up
        await Promise.all(done.map(async (cb) => await cb(params)));
        if (transition.direction === 'in' && window.isMobile()) {
            enter.el.current.querySelector('.product-hero-gallery').classList.add('fix-flicker-ios');
        }
        enter.el.current.style.pointerEvents = 'all';
        enter.el.current.style.scale = '';
        enter.done();
        resetStore();
        if (transition.direction === 'out') {
            header === null || header === void 0 ? void 0 : header.classList.remove('header--pdp');
        }
        header === null || header === void 0 ? void 0 : header.classList.remove('transitioning');
        if (transition.direction === 'in' && window.isMobile()) {
            setTimeout(() => {
                enter.el.current.querySelector('.product-hero-gallery').classList.remove('fix-flicker-ios');
            }, 150);
        }
    };
    // !SECTION End Push
    const getCustomTime = () => {
        const cookiename = 'devtranstime';
        let name = cookiename + '=';
        let decodedCookie = decodeURIComponent(document.cookie);
        let ca = decodedCookie.split(';');
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) == ' ') {
                c = c.substring(1);
            }
            if (c.indexOf(name) == 0) {
                return parseInt(c.substring(name.length, c.length));
            }
        }
        return null;
    };
    const CUSTOM_TIME = getCustomTime();
    const TOTAL_TIME = CUSTOM_TIME || 1.2;
    const handleTime = (time) => {
        return (time / 12) * TOTAL_TIME;
    };
    // !SECTION End Zoomout
    // SECTION Zoom in --------------------------------------------------------------------
    const handleZoomIn = async (params) => {
        var _a, _b;
        const { setup, animate, done } = playbackHooks.current;
        const { enter, exit, transition } = params;
        const { state } = location;
        const header = document.getElementsByTagName('header')[0];
        const tickerBar = document.querySelector('.ticker-bar');
        const catalogContainer = document.getElementById('catalog');
        // const stickyTray = document.querySelector('.sticky-tray') as HTMLElement;
        const exitTile = document.querySelector(`[data-productuuid="${state.transition.productUuid}"]`);
        // Setup
        catalogContainer.style.overflow = 'hidden';
        if (transition.direction === 'in') {
            bodyHeight.current = `${catalogContainer.offsetHeight}`;
        }
        else {
            catalogContainer.style.height = `${bodyHeight.current}px`;
        }
        if (header) {
            header.classList.add('header--pdp');
            header.classList.add('transitioning');
        }
        await Promise.all(setup.map(async (cb) => await cb(params)));
        const availableEnterAnimatedImages = enter.animatedImages.filter((image) => image.productId);
        const enterImagesCount = availableEnterAnimatedImages.length;
        let enterImage;
        if (enterImagesCount === 1) {
            enterImage = availableEnterAnimatedImages[0];
        }
        else {
            enterImage = availableEnterAnimatedImages.find((i) => {
                return i.productId === state.transition.productId;
            });
        }
        const exitImage = exit.animatedImages.find((image) => {
            return image.productUuid === state.transition.productUuid;
        });
        //check if page is not ready
        if (!((_a = enterImage === null || enterImage === void 0 ? void 0 : enterImage.el) === null || _a === void 0 ? void 0 : _a.current) || !((_b = exitImage === null || exitImage === void 0 ? void 0 : exitImage.el) === null || _b === void 0 ? void 0 : _b.current)) {
            console.warn('ANIMATION SKIPPED FOR AJAX PROBLEM');
            skipAnimation(params);
            await resetIntermediary();
            return;
        }
        const exitingImageSrc = enterImage.el.current.dataset.qualityImg;
        try {
            await setIntermediarySrc(exitingImageSrc);
        }
        catch (error) {
            console.log('intermediaryImage Decoding error', error);
            skipAnimation(params);
            await resetIntermediary();
            return;
        }
        // ANCHOR zoomIN standard
        const exitRect = exitImage.el.current.getBoundingClientRect(); // Repaint
        const exitTileRect = exitTile.getBoundingClientRect(); // Repaint
        const enterRect = enterImage.el.current.getBoundingClientRect(); // Repaint
        const tickerHeight = tickerBar ? tickerBar.offsetHeight : 0;
        // First we need to calculate the final scale (i.e. how much bigger will be the tile once in PDP ) in order to set the goal scale and max-width for the intermediary image
        // To do so, we need to decide if base the finale scale on the PDP's image width or height
        // That's because the PDP image can be cropped/cut horizontally or vertically depending on the viewport ratio
        // First we calculate the original aspect ratio
        const imageAspectRatio = exitRect.height / exitRect.width; // it's kinda hardcoded to 1.5 (we have some 150% padding in the CSS) but we calculate it anyway
        // Actually we can't use the viewport ratio, but the available space, ie minus the ticker bar and sticky button
        const pdpStickyTray = enter.el.current.querySelector('.sticky-tray__button .button');
        const pdpStickyTrayHeight = pdpStickyTray ? parseInt(window.getComputedStyle(pdpStickyTray).height) : 0;
        // Also we use document.documentElement.clientHeight instead of window.innerHeight because safari's url bar is unpredictable
        const windowAspectRatio = (document.documentElement.clientHeight - tickerHeight - pdpStickyTrayHeight) / window.innerWidth;
        // We prepare some variables, we'll them'em later
        let finalScale;
        let scale;
        let finalLeft = 0;
        // If window aspect ratio is lesser than imageAspectRatio we can take the width in account ( it's a lot easier this way)
        if (windowAspectRatio <= imageAspectRatio) {
            // We calculate the exit tile's scale for the CSS transition+transfrom, based on ratio between exitTile and PDP image's heights
            scale = exitRect.width / enterRect.width;
            intermediaryImg.el.current.style.setProperty('max-width', '100%');
        }
        else {
            // Otherwise, the height is preserved but the width is cropped in PDP, so we must use the height for our calculations with some extra steps:
            // We need to find how much bigger than 100% is the PDP's image:
            // An image which would perfectly fit the viewport's width (scale 100%), would have this height:
            const standardHeight = window.innerWidth * imageAspectRatio;
            // So to find out the PDP's scale, we use this proportion:
            // ( enterHeight : standardHeight = finalScale : 100 )
            // Or that would be the case, but since we need to take in account safari's url bar going up and down and altering enterRect's height, we also have to take that in account
            const realEnterRectHeight = enterRect.height - (window.innerHeight - document.documentElement.clientHeight);
            finalScale = realEnterRectHeight / standardHeight;
            // And then in percentage... but we also don't want the maxWidth to ever go lower than 100% ( this happens on older/smaller screens in which the PDP image is not cut horizontally )
            const finalMaxWidth = finalScale * 100;
            intermediaryImg.el.current.style.setProperty('max-width', `${finalMaxWidth}%`);
            // Also we calculate the exit tile's scale for the CSS transition+transfrom, based on ratio between exitTile and PDP image's heights
            scale = exitRect.height / realEnterRectHeight;
            // We also need to adjust the left, because since the PDP image is scaled up, we need to center crop the image
            finalLeft = (enterRect.width * finalScale - window.innerWidth) / 2;
        }
        intermediaryImg.el.current.style.setProperty('top', `${tickerHeight}px`);
        // Calculate the transform origin needed to overlap the intermediary with og tile
        // The transform origin X should be the tile's left coordinate but since we use as a reference the PDP's scaled up image, we need to adjust the number accordingly
        const xAnchor = exitTileRect.left / (1 - scale) + 'px';
        // Same for transform origin Y, but we also take the tickerHeight in account
        const yAnchor = (exitTileRect.top - tickerHeight) / (1 - scale) + 'px';
        intermediaryImg.el.current.style.setProperty('transform-origin', `${xAnchor} ${yAnchor}`);
        intermediaryImg.el.current.style.setProperty('transform', `scale(${scale})`);
        intermediaryImg.el.current.classList.add('shown');
        await new Promise((resolve) => {
            setTimeout(() => {
                resolve(1);
            }, 100);
        });
        // animazioni di setup
        enter.animation.set({
            pointerEvents: 'all',
            y: 0,
            zIndex: 10,
        });
        await Promise.all(animate.map(async (cb) => await cb(params)));
        // Actual Transitions
        // ANCHOR * INTERMEDIARY 🌆 --------------------------------------------------------------------
        const fadeBackdrop = intermediaryWrapper.current.querySelector('.fade-backdrop');
        fadeBackdrop.classList.add('transitioning');
        // Then we let the GPU animation do it's work by scaling up to normal ( 1 )
        intermediaryImg.el.current.style.setProperty('transform', `scale(1)`);
        // NOTE: left is not handled by GPU, so some hiccups can be found there : I previously tried with translateX but that would case an uwanted "curve" in tile's movement, but since a lot is changed, you could try again
        intermediaryImg.el.current.style.setProperty('left', `-${finalLeft}px`);
        // Apparently sometimes transitionend would not fire correctly, so we added a fallbacktimeout:
        // the first to finish will end the await
        const animationEndPromise = new Promise((resolve) => {
            intermediaryImg.el.current.addEventListener('transitionend', () => {
                setTimeout(() => {
                    resolve('animationEndPromise');
                }, 150);
            }, { once: true });
        });
        const fallbackTimeout = new Promise((resolve, reject) => {
            setTimeout(resolve, 1000, 'fallbackTimeout');
        });
        await Promise.race([animationEndPromise, fallbackTimeout]).then((value) => {
            console.log(value);
        });
        // Top:1 is to not trigger safari iphone's bar to appear
        // @ts-ignore scrollBehavior should accepts 'instant' indeed
        window.scroll({ top: 1, left: 0, behavior: 'instant' });
        // ANCHOR * EXIT ❌ --------------------------------------------------------------------
        exit.done();
        // ANCHOR * ENTER ✅ --------------------------------------------------------------------
        await enter.animation.start({
            opacity: 1,
            transition: {
                duration: handleTime(3),
            },
        });
        // ANCHOR * CLEAN UP ⭐️ -------------------------------------------------------------------
        resetIntermediary();
        enter.animation.set({
            position: 'static',
        });
        header === null || header === void 0 ? void 0 : header.classList.remove('transitioning');
        catalogContainer.style.removeProperty('overflow');
        catalogContainer.style.removeProperty('height');
        // We can now scroll to 0, a little scroll doesn't trigger safari iphone's bar to appear
        // @ts-ignore scrollBehavior should accepts 'instant' indeed
        window.scroll({ top: 0, left: 0, behavior: 'instant' });
        enter.done();
        await Promise.all(done.map(async (cb) => await cb(params)));
        resetStore();
    };
    // !SECTION
    const submitEnter = async (enter) => {
        if (firstRender.current) {
            const { appMounted } = playbackHooks.current;
            await Promise.all(appMounted.map(async (cb) => {
                await cb();
            }));
            firstRender.current = false;
        }
        else {
            store.enter = enter;
            ready();
        }
    };
    const submitExit = (exit) => {
        store.exit = exit;
        ready();
    };
    // ANCHOR - Ready --------------------------------------------------------------------
    const ready = async () => {
        const { state } = location;
        const { exit, enter } = store;
        if (!exit || !enter)
            return;
        // OE-14793 - PLP click on color swatch not works the zoom()
        const animationObject = {
            exit,
            enter,
            viewport: state === null || state === void 0 ? void 0 : state.viewport,
            transition: state === null || state === void 0 ? void 0 : state.transition,
        };
        if (window.tileToPlpTransition !== 'none') {
            if (window.tileToPlpTransition === 'push') {
                await push(animationObject);
            }
            else if (window.tileToPlpTransition === 'scale') {
                if (state.transition.direction === 'in' && window.innerWidth < window.mqObj['md']) {
                    await handleZoomIn(animationObject);
                }
                else {
                    await push(animationObject);
                }
            }
        }
        else {
            await noTransition(animationObject);
        }
        if (state.transition.direction === 'out') {
            ScrollTrigger.disable();
        }
        else {
            ScrollTrigger.refresh();
        }
        // update global variable in order to force chat refresh
        //@ts-ignore
        if (window.chatObject)
            window.chatObject.value = new Date().getTime();
    };
    const context = {
        submitExit,
        submitEnter,
        subscribe,
        unsubscribe,
    };
    // Render
    return (React.createElement(AnimatedContext.Provider, { value: context },
        React.createElement("div", { ref: intermediaryWrapper, className: "intermediary-wrapper" },
            React.createElement("img", { ref: intermediaryImg.el, src: "", alt: "", className: "intermediary-image" }),
            React.createElement("div", { className: "fade-backdrop" })),
        props.children));
};
export const useAnimatedContext = () => {
    return useContext(AnimatedContext);
};
export default AnimatedProvider;
